interface HoneybadgerNotice {
  backtrace?: { file?: string }[]
  tags?: string | string[]
}

const THIRD_PARTY_ORIGINS: string[] = [
  'googletagmanager.com',
  'google-analytics.com',
  'js.stripe.com',
  'static.hotjar.com',
  'script.hotjar.com'
]

export const THIRD_PARTY_TAG = 'third-party'

/**
 * Determines whether a notice is pure third-party script noise: every
 * identifiable frame comes from a known third-party origin, and no frame comes
 * from our own bundle. Neutral frames (`<anonymous>`, `<unknown>`, empty) are
 * ignored so a GTM stack that starts with `<anonymous>:1` still classifies.
 * @param notice - The Honeybadger notice being evaluated in `beforeNotify`.
 * @returns true when the error originates entirely from third-party scripts.
 */
export function isThirdParty(notice: HoneybadgerNotice): boolean {
  const frames = notice.backtrace ?? []
  if (frames.length === 0) return false

  let hasThirdParty = false

  for (const frame of frames) {
    const file = (frame.file || '').trim()
    if (['', '<anonymous>', '<unknown>'].includes(file)) continue

    if (THIRD_PARTY_ORIGINS.some((origin) => file.includes(origin))) {
      hasThirdParty = true
      continue
    }

    // A frame from our own bundle disqualifies the whole notice.
    return false
  }

  return hasThirdParty
}

/**
 * Normalizes Honeybadger's `string | string[]` tags union into a trimmed,
 * non-empty array of tags.
 * @param tags - The notice's tags in either form.
 * @returns The tags as a clean array.
 */
function normalizeTags(tags: HoneybadgerNotice['tags']): string[] {
  if (Array.isArray(tags)) {
    return tags
  }

  return (tags || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

/**
 * Appends a tag to the notice's tags without duplicating it.
 * @param notice - The Honeybadger notice to tag in place.
 * @param tag - The tag to append.
 */
export function applyTag(notice: HoneybadgerNotice, tag: string): void {
  const tags = normalizeTags(notice.tags)
  if (tags.includes(tag)) return

  notice.tags = [...tags, tag]
}

/**
 * Appends {@link THIRD_PARTY_TAG} to the notice's tags if the notice is third-party.
 * @param notice - The Honeybadger notice to tag in place.
 */
export function maybeApplyThirdPartyTag(notice: HoneybadgerNotice): void {
  if (isThirdParty(notice)) applyTag(notice, THIRD_PARTY_TAG)
}
